import { useState, useRef, useEffect } from 'react'
import { X, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MultiSelectInputProps {
  value: string[]
  onChange: (value: string[]) => void
  options?: string[]
  placeholder?: string
  disabled?: boolean
}

export function MultiSelectInput({
  value,
  onChange,
  options,
  placeholder = 'Add...',
  disabled,
}: MultiSelectInputProps) {
  const [inputVal, setInputVal] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const tags = Array.isArray(value) ? value : []

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  function addTag(tag: string) {
    const trimmed = tag.trim()
    if (trimmed && !tags.includes(trimmed)) {
      onChange([...tags, trimmed])
    }
    setInputVal('')
    setOpen(false)
  }

  function removeTag(tag: string) {
    onChange(tags.filter((t) => t !== tag))
  }

  const filteredOptions = options
    ? options.filter((o) => !tags.includes(o) && o.toLowerCase().includes(inputVal.toLowerCase()))
    : []

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={cn(
          'min-h-9 flex flex-wrap gap-1.5 p-1.5 rounded-lg border bg-slate-50 dark:bg-slate-700/50',
          'border-input dark:border-slate-600/60 focus-within:border-[#076698]/60',
          'focus-within:ring-2 focus-within:ring-[#076698]/20 transition-all',
          disabled && 'opacity-50 pointer-events-none'
        )}
      >
        {tags.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium
                       bg-[#076698]/10 text-[#076698] dark:bg-[#076698]/20 dark:text-[#5bb8e8]"
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              className="hover:text-red-500 transition-colors"
            >
              <X size={10} />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-20 bg-transparent text-sm outline-none placeholder:text-muted-foreground px-1"
          placeholder={tags.length === 0 ? placeholder : ''}
          value={inputVal}
          disabled={disabled}
          onChange={(e) => {
            setInputVal(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              if (inputVal.trim()) addTag(inputVal)
            } else if (e.key === 'Backspace' && !inputVal && tags.length > 0) {
              removeTag(tags[tags.length - 1])
            }
          }}
        />
        {options && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            className="self-center ml-auto px-1 text-muted-foreground hover:text-foreground"
          >
            <ChevronDown size={14} className={cn('transition-transform', open && 'rotate-180')} />
          </button>
        )}
      </div>

      {open && filteredOptions.length > 0 && (
        <div className="absolute z-50 top-full mt-1 left-0 w-full
                        glass-card rounded-xl border border-white/70 dark:border-white/10
                        shadow-xl overflow-hidden">
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addTag(opt) }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
